/**
 * Пределы допуска страниц к предварительной подготовке (router.prefetch
 * готового маршрута в кэше Next). Это ограничения исходных документов и
 * настроек, а не обещание точного размера памяти Next. Ответов с товарами,
 * заказами и смонтированных редакторов здесь нет.
 *
 * До 2026-09-20 готовились три страницы на пять минут; при быстрых переходах
 * по вкладкам большинство страниц не успевало подготовиться, и клик ждал
 * сборку маршрута на сервере («Отменить переход»). Теперь готовятся все
 * страницы аккаунта в порядке вероятности перехода (набор вкладок → избранное
 * → недавние → остальные), небольшими порциями в тишине, и живут полчаса.
 * У владельца 37 страниц, все документы вместе 125 КБ — это единицы мегабайт
 * в кэше маршрутов, против 35 МБ одной собранной таблицы.
 */

/** Сколько страниц одного аккаунта может быть подготовлено (и в очереди, и в кэше). */
export const WORKSPACE_PREPARE_MAX_PAGES = 200;
/**
 * Первые кандидаты получают метку свежести (SHA-256 снимка страницы): с ней
 * проверка при клике сразу возвращает новую страницу, если её кто-то менял.
 * Метка требует собрать оболочку страницы на сервере, поэтому только для
 * самых вероятных переходов; остальные проверяются по метке из самого маршрута.
 */
export const WORKSPACE_PREPARE_TAGGED = 12;
/** Срок жизни подготовки в этой вкладке; равен staleTimes.static в next.config.mjs. */
export const WORKSPACE_PREPARE_TTL_MS = 30 * 60_000;
export const WORKSPACE_PREPARE_DOC_BYTES = 256 * 1024;
export const WORKSPACE_PREPARE_TOTAL_BYTES = 8 * 1024 * 1024;
/** Очередь prefetch: по столько адресов за такт, чтобы не выстрелить всеми разом. */
export const WORKSPACE_PREPARE_BATCH = 2;
export const WORKSPACE_PREPARE_BATCH_INTERVAL_MS = 400;
/** Подготовка стартует после такой паузы без кликов, клавиш и прокрутки. */
export const WORKSPACE_PREPARE_IDLE_MS = 1500;

export type PreparedWorkspaceCandidate = { id: string; nodeTypes: string[]; snapshot: string | null };
export type WorkspacePreparationResult = { identity: string; pages: PreparedWorkspaceCandidate[] };

export function selectWorkspacePreparation<T extends { id: string; bytes: number }>(rows: readonly T[], currentId: string): T[] {
  const result: T[] = [];
  let bytes = 0;
  const seen = new Set<string>();
  for (const row of rows) {
    if (row.id === currentId || seen.has(row.id) || row.bytes < 0 || !Number.isFinite(row.bytes)
      || row.bytes > WORKSPACE_PREPARE_DOC_BYTES || bytes + row.bytes > WORKSPACE_PREPARE_TOTAL_BYTES) continue;
    result.push(row); seen.add(row.id); bytes += row.bytes;
    if (result.length === WORKSPACE_PREPARE_MAX_PAGES) break;
  }
  return result;
}
